'use client'
import TestCard from '@/components/common/TestCard'
import { Swiper, SwiperSlide } from 'swiper/react'
import { ArrowRight } from 'lucide-react'
import { Autoplay, Navigation } from 'swiper/modules'
import { dispatch, useSelector } from '@/lib/store'
import { updatePackageData } from '@/lib/reducers/packageReducer'
import PackageCardSkeleton from '../common/Skeleton/PackageCardSkeleton'
import { useEffect, useRef, useState } from 'react'
import { Swiper as SwiperType } from 'swiper'
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs'
import CarouselNavigation from '@/components/common/CarouselNavigation'
import Link from 'next/link'
import {
  getTitleCaseNameBySlug,
  getPopularHealthCheckupUrl,
} from '@/utils/commonFunc'
import { useMediaQuery } from '@/hooks/use-media-query'
import { consumerRequest } from '@/helper/request'
import { POPULAR_HEALTH_CHECKUPS_API } from '@/helper/apiConstants'
import { CURRENT_SUBSIDIARY, DEFAULT_CITY_SLUG } from '@/utils/constants'

interface MostPopularHealthCheckupsProps {
  ssrData?: Record<string, any[]> | null
  ssrCityUuid?: string
}

const MostPopularHealthCheckups = ({
  ssrData = null,
  ssrCityUuid,
}: MostPopularHealthCheckupsProps) => {
  const { mounted } = useMediaQuery('(min-width: 768px)')
  const cityState = useSelector((state) => state.city) || {}
  const swiperRef = useRef<SwiperType | null>(null)
  const [activeToggle, setActiveToggle] = useState('Tests')
  const [category, setCategory] = useState('MOST_POPULAR_TEST')
  const [data, setData] = useState<Record<string, any[]> | null>(ssrData)

  useEffect(() => {
    const mostPopularTest = ssrData?.['MOST_POPULAR_TEST']
    const mostPopularPackage = ssrData?.['MOST_POPULAR_PACKAGE']
    const payload: Record<string, any> = {}
    if (mostPopularTest?.length) payload.mostPopularTest = mostPopularTest
    if (mostPopularPackage?.length)
      payload.mostPopularPackage = mostPopularPackage
    if (Object.keys(payload).length) dispatch(updatePackageData(payload))
  }, [])

  // Refetch if the user's selected city differs from the SSR city (prices vary per city).
  useEffect(() => {
    const selectedUuid = cityState?.currentCityObj?.uuid
    const selectedSlug = cityState?.currentCityObj?.slugName
    if (!selectedUuid || !ssrCityUuid || selectedUuid === ssrCityUuid) return

    const controller = new AbortController()
    ;(async () => {
      try {
        const apiRes: any = await consumerRequest(
          POPULAR_HEALTH_CHECKUPS_API,
          'post',
          {
            subsidiary: CURRENT_SUBSIDIARY,
            cityUuid: selectedUuid,
            citySlugName: selectedSlug,
          },
          undefined,
          controller.signal
        )
        if (!apiRes?.status) return
        setData(apiRes?.data || null)
        dispatch(
          updatePackageData({
            mostPopularTest: apiRes?.data?.MOST_POPULAR_TEST || [],
            mostPopularPackage: apiRes?.data?.MOST_POPULAR_PACKAGE || [],
          })
        )
      } catch (err: any) {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return
        // Rethrowing here becomes an unhandled rejection no error boundary can
        // catch - the SSR data stays on screen instead.
        console.error('Failed to refetch popular health checkups', err)
      }
    })()
    return () => controller.abort()
  }, [cityState?.currentCityObj?.uuid, ssrCityUuid])

  const categoryData = data?.[category]
  const mostPopularHealthCheckupData = Array.isArray(categoryData)
    ? categoryData
    : []

  const goToNextSlide = () => {
    if (swiperRef.current) swiperRef.current.slideNext()
  }

  const goToPrevSlide = () => {
    if (swiperRef.current) swiperRef.current.slidePrev()
  }

  const citySlug =
    (mounted ? cityState?.currentCityObj?.slugName : undefined) ||
    DEFAULT_CITY_SLUG
  const healthCheckupUrl = getPopularHealthCheckupUrl(citySlug)
  const viewAllLabel = `View all health checkups in ${getTitleCaseNameBySlug(citySlug)}`

  return (
    <section className="container max-w-full py-6 md:py-10 bg-white">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-heading-md md:text-heading-lg text-gray-900">
            Most Popular Health Checkups
          </h3>
        </div>

        <div className="items-center gap-4 hidden md:flex lg:flex xl:flex">
          <Link
            href={healthCheckupUrl}
            aria-label={viewAllLabel}
            className="flex items-center gap-2 font-semibold text-label-md tracking-wide bg-white px-4 py-2 rounded-md text-primary-600 border border-primary-100"
          >
            View all
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
      <Tabs
        defaultValue="Tests"
        value={activeToggle}
        onValueChange={(value) => {
          setActiveToggle(value)
          setCategory(
            value === 'Tests' ? 'MOST_POPULAR_TEST' : 'MOST_POPULAR_PACKAGE'
          )
        }}
        className="mb-6 sm:mb-8 w-fit"
      >
        <TabsList className="rounded-full p-1 border border-gray-300 bg-white h-auto">
          <TabsTrigger
            value="Tests"
            className="h-[32px] w-[92px] lg:h-[36px] lg:w-[100px] text-body-xs lg:text-label-sm rounded-full data-[state=active]:bg-gray-900 data-[state=active]:text-white data-[state=inactive]:text-gray-500 data-[state=inactive]:bg-white shadow-none"
          >
            Tests
          </TabsTrigger>
          <TabsTrigger
            value="Packages"
            className="h-[32px] w-[92px] lg:h-[36px] lg:w-[100px] text-body-xs lg:text-label-sm rounded-full data-[state=active]:bg-gray-900 data-[state=active]:text-white data-[state=inactive]:text-gray-500 data-[state=inactive]:bg-white shadow-none"
          >
            Packages
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="test-outer-wrap relative">
        {!mostPopularHealthCheckupData?.length ? (
          <PackageCardSkeleton />
        ) : !mounted ? (
          <div className="flex gap-5 overflow-hidden">
            {mostPopularHealthCheckupData
              .filter((item: any) => item?.productDto)
              .slice(0, 4)
              .map((item: any) => (
                <div
                  key={item.productDto.id}
                  className="flex-shrink-0 w-[308px]"
                >
                  <TestCard data={item} />
                </div>
              ))}
          </div>
        ) : (
          <>
            <CarouselNavigation
              goToPrevSlide={goToPrevSlide}
              goToNextSlide={goToNextSlide}
            />
            <Swiper
              onSwiper={(swiper) => (swiperRef.current = swiper)}
              navigation
              slidesPerView="auto"
              spaceBetween={20}
              className="navigation_hidden"
              modules={[Autoplay, Navigation]}
            >
              {mostPopularHealthCheckupData
                .filter((item: any) => item?.productDto)
                .map((item: any) => (
                  <SwiperSlide
                    key={item.productDto.id}
                    className="h_auto !w-[308px]"
                  >
                    <TestCard data={item} />
                  </SwiperSlide>
                ))}
            </Swiper>
            <Link
              href={healthCheckupUrl}
              aria-label={viewAllLabel}
              className="mt-6 w-full lg:hidden flex items-center justify-center gap-2 font-semibold text-label-md tracking-wide uppercase bg-white py-3 rounded-md border border-primary-100 text-primary-600 hover:text-primary-700 transition-colors duration-200"
            >
              View all
              <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
      </div>
    </section>
  )
}

export default MostPopularHealthCheckups
